<template>
  <div
    class="d-flex"
    :class="{
      'flex-row align-center': !column,
      'flex-column': column,
    }"
  >

    <!--desktop version-->
    <template v-if="true">
      <slot name="prepend">
        <div
          class="pa-2 pl-0 text-no-wrap"
          :class="prependTitleClass"
        >
          {{ prependTitle }}
        </div>
      </slot>

      <template v-if="dropdown && !multiple">
        <app-card
          class="d-flex flex-row align-center inputBg"
          :width="dropdownWidth"
          color="transparent"
        >
          <app-select
            ref="selector"
            :value="activeFirst.id"
            hide-details
            :items="itemsV"
            @change="toggleItem($event)"
            item-text="title"
            item-value="id"
            class="z-1"
          >
            <template #item="{ item }">
              {{ item.title }}
            </template>
          </app-select>
          <app-button
            text
            :height="36"
            width="42"
            min-width="30"
            style="margin-left: -1px;"
            class="px-2 outlined border--color--dividers rounded-0"
            color="primary"
            @click="toggleItemSortType(activeFirst.id)"
            @click.stop
          >
            <v-icon v-if="activeFirst.ordering === OrderingEnum.ASC">
              {{ activeFirst.iconAsc || sortIconAsc }}
            </v-icon>
            <v-icon v-else-if="activeFirst.ordering === OrderingEnum.DESC">
              {{ activeFirst.iconDesc || sortIconDesc }}
            </v-icon>
          </app-button>
        </app-card>
      </template>
      <template v-else>
        <slot
          v-for="{ id, title, active, ordering, iconAsc, iconDesc } in itemsV"
          name="item"
          :id="id"
          :title="title"
          :active="active"
          :ordering="ordering"
          :iconAsc="iconAsc || sortIconAsc"
          :iconDesc="iconDesc || sortIconDesc"
        >
          <app-card
            :key="id"
            class="mx-1 pa-1 d-flex flex-row flex-wrap align-center font-weight-medium rounded"
            :class="{
            'primary--text font-weight-bold': active,
            }"
            @click="toggleItem(id)"
          >
            <v-hover v-slot="{ hover }">
              <div
                class="px-1 text-hyphens overflow-auto"
                :class="{ 'primary--text': hover }"
              >
                {{ title }}
              </div>
            </v-hover>
            <v-spacer/>
            <app-button
              v-if="active"
              text
              height="24"
              width="32"
              min-width="30"
              class="px-0"
              :color="active ? 'primary' : 'white'"
              hover-class="outlined"
              @click="toggleItemSortType(id)"
              @click.stop
            >
              <v-icon v-if="ordering === OrderingEnum.ASC">
                {{ iconAsc || sortIconAsc }}
              </v-icon>
              <v-icon v-else-if="ordering === OrderingEnum.DESC">
                {{ iconDesc || sortIconDesc }}
              </v-icon>
            </app-button>
          </app-card>
        </slot>
      </template>
      <slot name="append"/>
    </template>


    <!--mobile version-->
    <template v-else>
      <v-dialog
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
        v-model="dialog"
      >
        <template v-slot:activator="{ on }">
          <app-button
            icon
            no-tooltip
            main-icon="mdi-filter-outline"
            v-on="on"
          />
        </template>
        <v-card>
          <v-card-title>
            {{ prependTitle }}
          </v-card-title>
          <v-card-text>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
              <v-spacer/>
              <v-btn
                color="primary"
                text
                @click="dialog = false"
              >
                Done
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
    </template>

  </div>
</template>

<script>
  /**
    items - Array of objects with such structure:
    {
      id,
      title,
      iconAsc,  - optional
      iconDesc, - optional
    }

    value - Object from items array with addition info:
    {
      id,
      title,
      ordering,
      short
    }
  */
  const OrderingEnum = Object.freeze({ ASC: '+', DESC: '-' });

  export default {
    name: 'SortingBar',

    props: {
      value: {
        type: Array,
        default: () => ([]),
      },
      items: {
        type: Array,
        default: () => [],
      },
      dropdown: {
        type: Boolean,
        default: false,
      },
      dropdownWidth: {
        type: [String, Number],
      },
      column: {
        type: Boolean,
        default: false,
      },
      prependTitle: {
        type: String,
        default: 'Sorting by:',
      },
      prependTitleClass: {
        type: [String, Array, Object],
        default: 'title',
      },
      sortIconAsc: {
        type: String,
        default: 'mdi-sort-ascending mdi-flip-v'
      },
      sortIconDesc: {
        type: String,
        default: 'mdi-sort-descending mdi-flip-v'
      },
      multiple: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        itemsV: [],
        OrderingEnum,
        dialog: false
      };
    },

    computed: {
      active() {
        return this.itemsV.filter( it => it.active )
            .map( ({ id, ordering }) => ({ id, ordering }) );
      },

      activeFirst() {
        return this.active[0];
      },
    },

    watch: {
      items: {
        deep: true,
        immediate: true,
        handler(items) {
          this.itemsV = [ ...items ];
        },
      },

      value: {
        deep: true,
        immediate: true,
        handler(v) {
          if (!Array.isArray(v)) v = [v];
          this.updateActive(v);
        },
      },

      multiple: {
        immediate: true,
        handler(v) {
          if (v) return;
          if (this.countActive() < 2) return;
          this.setUnactive(this.itemsV.find( it => it.active )?.id);
        },
      },

      active: {
        immediate: true,
        handler(v) {
          this.$emit('input', v);
        },
      },
    },

    methods: {
      toggleItem(id) {
        const { itemsV, multiple } = this;
        let found = itemsV.find( it => it.id === id );
        if (!found) return;

        if (!multiple) {
          let ingoreIds = [found.id]
          if (found.active) {
            // save first active
            ingoreIds.push(itemsV.find( it => it.active )?.id)
          }
          this.setUnactive(ingoreIds);
        }

        this.$set(found, 'active', !found.active);
        this.$set(found, 'ordering', OrderingEnum.ASC);
      },

      toggleItemSortType(id) {
        let found = this.items.find( it => it.id === id );
        if (!found) return;
        if (!found.active) return;

        if (found.ordering === OrderingEnum.ASC || !found.ordering) {
          this.$set(found, 'ordering', OrderingEnum.DESC);
          return;
        }
        if (found.ordering === OrderingEnum.DESC) {
          this.$set(found, 'ordering', OrderingEnum.ASC);
          return;
        }
      },

      countActive() {
        return this.itemsV.filter( it => it.active ).length;
      },

      setUnactive(ignoreIdOrIds) {
        if (!Array.isArray(ignoreIdOrIds)) ignoreIdOrIds = [ignoreIdOrIds];
        this.itemsV.filter( it => it.active )
            .filter( it => !ignoreIdOrIds.includes(it.id) )
            .forEach( it => this.$set(it, 'active', false));
      },

      updateActive(newActive) {
        let ids = newActive.map( it => it.id );
        this.setUnactive(ids);

        newActive.forEach( ({ id, ordering }) => {
          let found = this.itemsV.find( it => it.id === id );
          if (!found) return;
          if (!found.active) this.$set(found, 'active', true);
          if (!Object.values(OrderingEnum).includes(ordering)) ordering = OrderingEnum.ASC;
          if (found.ordering === ordering) return;
          this.$set(found, 'ordering', ordering);
        })
      },
    }
  }
</script>